import React, {
  FC,
  createContext,
  useState,
  useEffect,
  useRef,
  useContext
} from "react";
import cn from "clsx";
import isEmpty from "lodash/isEmpty";
import difference from "lodash/difference";
import groupBy from "lodash/groupBy";
import { Scrollbars } from "react-custom-scrollbars";
import { selectedFiltersContext } from ".";
import { getDeepSelectedCategory } from "@/utils";
import { CATEGORY_QUERY_PARAMS } from "../constants";
import { CategoryFilterOption, FilterType, GeneralFacet } from "../types";
import {
  dropShallowRowsCoveredByDeeper,
  stripCategoryDeeperThanAnchor
} from "../utils/pruneOrphanedSizeSelections";
import SquareRadio from "@/components/squareRadio";

type FilterCategoryPropsType = {
  filter: Omit<GeneralFacet & { data: CategoryFilterOption[] }, "sortOrder">;
  setToggledFilter: (isToggle: any) => void;
  toggledFilter: FilterType;
  onApplyButtonClick: (...args) => void;
};

export const selectedCategoryFilterContext = createContext<{
  selectedOptions: any;
  onSelection: (...args) => void;
  selectedDropDown: { [val: string]: string };
  setSelectedDropDown: (...args) => void;
  hasAutoExpanded: React.MutableRefObject<boolean>;
}>({
  selectedOptions: [],
  onSelection: (...args) => {},
  selectedDropDown: {},
  setSelectedDropDown: (...args) => {},
  hasAutoExpanded: { current: false }
});

const FilterCategoryMob: FC<FilterCategoryPropsType> = ({
  filter,
  setToggledFilter,
  toggledFilter,
  onApplyButtonClick
}) => {
  const [selectedCategoryOptions, setSelectedCategoryOptions] = useState<any>(
    []
  );
  const [selectedDropDown, setSelectedDropDown] = useState<{
    [val: string]: string;
  }>({});
  const hasAutoExpanded = useRef(false);
  const { isFiltersOpen, selectedFilters } = useContext(selectedFiltersContext);

  const [drillItem, setDrillItem] = useState<CategoryFilterOption | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  useEffect(() => {
    if (isFiltersOpen) {
      const timeoutId = setTimeout(() => {
        hasAutoExpanded.current = true;
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      hasAutoExpanded.current = false;
      setDrillItem(null);
      setExpandedKey(null);
    }
  }, [isFiltersOpen]);

  useEffect(() => {
    setSelectedCategoryOptions(getDeepSelectedCategory(filter));
  }, [filter]);

  const commitCategorySelection = (
    newFlatSelection: CategoryFilterOption[]
  ) => {
    if (isEmpty(newFlatSelection)) {
      setSelectedCategoryOptions([]);
      onApplyButtonClick(
        CATEGORY_QUERY_PARAMS.map(qp => ({
          ...filter,
          queryParam: qp,
          selectedOptions: []
        }))
      );
      return;
    }

    const pruned = dropShallowRowsCoveredByDeeper(newFlatSelection);
    setSelectedCategoryOptions(pruned);
    const grouped = groupBy(pruned, "queryParam");
    onApplyButtonClick(
      CATEGORY_QUERY_PARAMS.map(qp => ({
        ...filter,
        queryParam: qp,
        selectedOptions: grouped[qp] || []
      }))
    );
  };

  const mergePreservedSameQueryParam = (
    queryParam: string,
    nextForParam: CategoryFilterOption[],
    sameLevelBeforeToggle: CategoryFilterOption[],
    clickedOption: CategoryFilterOption
  ): CategoryFilterOption[] => {
    const contextOpts = selectedFilters[queryParam]?.selectedOptions;
    if (!Array.isArray(contextOpts)) return nextForParam;

    const knownKeys = new Set(sameLevelBeforeToggle.map(o => String(o.key)));
    knownKeys.add(String(clickedOption.key));

    const preserved = contextOpts.filter(
      o => o?.key != null && !knownKeys.has(String(o.key))
    );

    if (preserved.length === 0) return nextForParam;

    return [...nextForParam, ...(preserved as CategoryFilterOption[])];
  };

  const onFilterOptionClick = ({ option, isLeaf = false }, event?) => {
    event?.stopPropagation();

    const findOptionInArray = (optionArray: any[]) =>
      optionArray.find(existingOption => existingOption.key === option.key);

    if (isLeaf) {
      const filteredOptions = selectedCategoryOptions.filter(
        existingOption => existingOption.queryParam === option.queryParam
      );
      const isAlreadySelected = findOptionInArray(filteredOptions);

      let options: CategoryFilterOption[];
      if (isAlreadySelected) {
        options = filteredOptions.filter(item => item.key !== option.key);
      } else {
        options = [...filteredOptions, option];
      }
      options = mergePreservedSameQueryParam(
        option.queryParam,
        options,
        filteredOptions,
        option
      );

      const findDescendantSelections = (
        parentOption,
        currentSelection: any[]
      ) => {
        if (isEmpty(parentOption.children)) return [];
        if (
          currentSelection.find(
            selectedOption => selectedOption.key === parentOption.key
          )
        ) {
          return [parentOption];
        }

        const selectedChildren = parentOption.children.filter(child =>
          currentSelection.find(
            selectedOption => selectedOption.key === child.key
          )
        );
        return !isEmpty(selectedChildren)
          ? selectedChildren
          : parentOption.children.flatMap(child =>
              findDescendantSelections(child, currentSelection)
            );
      };

      const findAncestorSelection = (
        clickedOption,
        currentSelection: any[]
      ) => {
        if (isEmpty(currentSelection)) return null;
        return currentSelection.find(
          selectedOption =>
            !isEmpty(findDescendantSelections(selectedOption, [clickedOption]))
        );
      };

      const selectedAncestor = findAncestorSelection(
        option,
        selectedCategoryOptions
      );

      if (!isAlreadySelected && !selectedAncestor && isFullySelected(option)) {
        // All of option's children are selected but option itself is not a direct item.
        // User clicked a virtually-checked item → deselect all its descendants.
        const newFlat = selectedCategoryOptions.filter(
          s => !findDescendant(option, s.key)
        );
        commitCategorySelection(newFlat);
        return;
      }

      if (!isAlreadySelected && selectedAncestor) {
        // User deselected a child that was covered by a selected ancestor.
        // Expand ancestor to all descendants except the deselected one.
        const remaining = getDescendantsExcluding(selectedAncestor, option.key);
        const otherSelected = difference(selectedCategoryOptions, [
          selectedAncestor
        ]);
        commitCategorySelection([...otherSelected, ...remaining]);
        return;
      }

      const descendantsToRemove =
        !isAlreadySelected &&
        isLeaf &&
        option.children &&
        findDescendantSelections(option, selectedCategoryOptions);

      if (!isEmpty(descendantsToRemove)) {
        const groupedRemove = groupBy(descendantsToRemove, "queryParam");
        const latestSelectedFilters = Object.keys(groupedRemove).reduce(
          (acc, key) => {
            acc[key] = {
              ...selectedFilters[key],
              selectedOptions: Array.isArray(
                selectedFilters[key]?.selectedOptions
              )
                ? selectedFilters[key].selectedOptions.filter(
                    selectedOption =>
                      !groupedRemove[key].find(
                        removedOption =>
                          removedOption.key === selectedOption.key
                      )
                  )
                : []
            };
            return acc;
          },
          { ...selectedFilters }
        );

        const qp = option.queryParam;
        const updates = CATEGORY_QUERY_PARAMS.map(qpKey => {
          if (qpKey === qp) {
            return {
              ...filter,
              queryParam: qpKey,
              defaultLabel: filter.defaultLabel,
              selectedOptions: isLeaf ? options : [option]
            };
          }
          const prev = latestSelectedFilters[qpKey];
          return {
            ...filter,
            queryParam: qpKey,
            defaultLabel: filter.defaultLabel,
            selectedOptions: Array.isArray(prev?.selectedOptions)
              ? prev.selectedOptions
              : []
          };
        });

        setSelectedCategoryOptions(
          difference(selectedCategoryOptions, descendantsToRemove)
        );
        onApplyButtonClick(updates);
        return;
      }

      let flat = [
        ...selectedCategoryOptions.filter(
          (s: CategoryFilterOption) => s.queryParam !== option.queryParam
        ),
        ...options
      ];
      if (!isAlreadySelected) {
        flat = stripCategoryDeeperThanAnchor(flat, option);
      }
      commitCategorySelection(flat);
      return;
    }

    if (isFullySelected(option)) {
      const newFlat = selectedCategoryOptions.filter(
        s => s.key !== option.key && !findDescendant(option, s.key)
      );
      commitCategorySelection(newFlat);
    } else {
      commitCategorySelection([option]);
    }
  };

  const isItemSelected = (option: CategoryFilterOption): boolean =>
    selectedCategoryOptions.some(s => s.key === option.key);

  const hasAnySelectedInSubtree = (option: CategoryFilterOption): boolean => {
    if (isItemSelected(option)) return true;
    return (option.children || []).some(c => hasAnySelectedInSubtree(c));
  };

  const isFullySelected = (option: CategoryFilterOption): boolean => {
    if (isItemSelected(option)) return true;
    const children = option.children || [];
    if (children.length === 0) return false;
    return children.every(c => isFullySelected(c));
  };

  const isPartiallySelected = (option: CategoryFilterOption): boolean => {
    if (isFullySelected(option)) return false;
    return hasAnySelectedInSubtree(option);
  };

  const findDescendant = (node: CategoryFilterOption, key: string): boolean => {
    if (node.key === key) return true;
    return (node.children || []).some(c => findDescendant(c, key));
  };

  // Expands a selected ancestor to all its descendants excluding the deselected key.
  // Handles arbitrary depth: if excluded is deeper, recursively expands that branch.
  const getDescendantsExcluding = (
    node: CategoryFilterOption,
    excludedKey: string
  ): CategoryFilterOption[] => {
    const children = node.children || [];
    const directExcluded = children.find(c => c.key === excludedKey);
    if (directExcluded) {
      return children.filter(c => c.key !== excludedKey);
    }
    const childWithExcluded = children.find(c =>
      findDescendant(c, excludedKey)
    );
    if (childWithExcluded) {
      const others = children.filter(c => c.key !== childWithExcluded.key);
      return [
        ...others,
        ...getDescendantsExcluding(childWithExcluded, excludedKey)
      ];
    }
    return children;
  };

  const currentLevelItems = drillItem ? drillItem.children ?? [] : filter.data;
  const expandedItem = expandedKey
    ? currentLevelItems.find(i => i.key === expandedKey) ?? null
    : null;

  const handleDrillIn = (item: CategoryFilterOption) => {
    setDrillItem(item);
    setExpandedKey(null);
  };

  const handleDrillBack = () => {
    setDrillItem(null);
    setExpandedKey(null);
  };

  const toggleExpand = (key: string) => {
    setExpandedKey(prev => (prev === key ? null : key));
  };

  const formatCount = (count?: number) =>
    count != null ? count.toLocaleString() : null;

  return (
    <div className="filter_content openLeft" onClick={e => e.stopPropagation()}>
      {/* Breadcrumb */}
      {drillItem && (
        <div className="cat_breadcrumb">
          <button className="cat_breadcrumb_back" onClick={handleDrillBack}>
            ›
          </button>
          <div className="cat_breadcrumb_trail">
            <span className="cat_breadcrumb_root">
              {filter.defaultLabel || "Category"}
            </span>
            <span className="cat_breadcrumb_sep">{" › "}</span>
            <span className="cat_breadcrumb_item cat_breadcrumb_bold">
              {drillItem.label}
            </span>
            {expandedItem && (
              <>
                <span className="cat_breadcrumb_sep">{" › "}</span>
                <span className="cat_breadcrumb_item">
                  {expandedItem.label}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="filter_content_scroll_host">
        <div className="filter_content_scroll_area">
          <Scrollbars style={{ width: "100%", height: "100%" }}>
            {/* All [parent] when drilled */}
            {drillItem && (
              <div
                className="cat_drill_item"
                onClick={e =>
                  onFilterOptionClick({ option: drillItem, isLeaf: false }, e)
                }
              >
                <SquareRadio
                  isChecked={isFullySelected(drillItem)}
                  isIndeterminate={isPartiallySelected(drillItem)}
                />
                <span
                  className={cn("cat_drill_label", {
                    cat_drill_label_bold: isItemSelected(drillItem)
                  })}
                >
                  {drillItem.extraLabel || `All ${drillItem.label}`}
                </span>
                {drillItem.count != null && (
                  <span className="cat_drill_count">
                    {formatCount(drillItem.count)}
                  </span>
                )}
              </div>
            )}

            {/* Current level items */}
            {currentLevelItems.map(option => {
              const selected =
                isItemSelected(option) || hasAnySelectedInSubtree(option);
              const isChecked = isFullySelected(option);
              const isPartial = isPartiallySelected(option);
              const hasChildren = (option.children?.length ?? 0) > 0;
              const isExpanded = expandedKey === option.key;

              return (
                <React.Fragment key={option.key}>
                  <div
                    className="cat_drill_item"
                    onClick={e =>
                      onFilterOptionClick({ option, isLeaf: true }, e)
                    }
                  >
                    <SquareRadio
                      isChecked={isChecked}
                      isIndeterminate={isPartial}
                    />
                    <span
                      className={cn("cat_drill_label", {
                        cat_drill_label_bold: selected
                      })}
                    >
                      {option.label}
                    </span>
                    {option.count != null && (
                      <span className="cat_drill_count">
                        {formatCount(option.count)}
                      </span>
                    )}
                    {hasChildren && !drillItem && (
                      <button
                        className="cat_drill_chevron"
                        onClick={e => {
                          e.stopPropagation();
                          handleDrillIn(option);
                        }}
                      >
                        ›
                      </button>
                    )}
                    {hasChildren && drillItem && (
                      <button
                        className={cn("cat_drill_chevron", {
                          cat_drill_chevron_up: isExpanded
                        })}
                        onClick={e => {
                          e.stopPropagation();
                          toggleExpand(option.key);
                        }}
                      >
                        <span
                          className={
                            isExpanded
                              ? "cat_chevron_icon cat_chevron_up"
                              : "cat_chevron_icon"
                          }
                        >
                          ›
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Accordion children */}
                  {isExpanded &&
                    option.children?.map(child => {
                      const childSelected = isFullySelected(child);
                      const childPartial = isPartiallySelected(child);
                      return (
                        <div
                          key={child.key}
                          className="cat_drill_item cat_drill_child"
                          onClick={e =>
                            onFilterOptionClick(
                              { option: child, isLeaf: true },
                              e
                            )
                          }
                        >
                          <SquareRadio
                            isChecked={childSelected}
                            isIndeterminate={childPartial}
                          />
                          <span
                            className={cn("cat_drill_label", {
                              cat_drill_label_bold: childSelected
                            })}
                          >
                            {child.label}
                          </span>
                          {child.count != null && (
                            <span className="cat_drill_count">
                              {formatCount(child.count)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

// Kept for legacy imports in filterCategoryDropDownList.tsx
export const RootFilterContent: FC<{ filterContent: any }> = () => null;

export default FilterCategoryMob;
